/**
 * ConfigPanel
 *
 * Read/write panel for referral reward configuration.
 * Styled using the shared AgenticPayment design system (styles.js).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getRequest, putRequest } from '../../utils/httpRequest';
import { API_ENDPOINTS } from './constants';
import { colors, gradients, commonStyles, buttonStyles } from '../AgenticPaymentTransactionHistory/styles';

// ── Local style tokens ────────────────────────────────────────────────────────

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  border: `1px solid ${colors.gray400}`,
  borderRadius: '4px',
  fontSize: '14px',
  color: colors.gray700,
  background: colors.white,
  boxSizing: 'border-box',
  outline: 'none',
  fontFamily: 'inherit',
};

const selectStyle = {
  ...inputStyle,
  paddingRight: '36px',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
  backgroundSize: '12px',
  appearance: 'none',
  WebkitAppearance: 'none',
  cursor: 'pointer',
};

const labelStyle = {
  ...commonStyles.label,
  marginBottom: '8px',
};

// ── Component ─────────────────────────────────────────────────────────────────

const ConfigPanel = ({ adminUserId }) => {
  const [config, setConfig]         = useState({ base_reward_amount_usd: '', reward_mode: '', approval_mode: '' });
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState(null);
  const [success, setSuccess]       = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const data = await getRequest(API_ENDPOINTS.GET_CONFIG);
      if (!data.success) throw new Error(data.error || 'Failed to load config');
      setConfig({
        base_reward_amount_usd: data.data?.base_reward_amount_usd ?? '',
        reward_mode:            data.data?.reward_mode ?? '',
        approval_mode:          data.data?.approval_mode ?? '',
      });
    } catch (err) {
      setFetchError(err.message || 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleChange = (field, value) => setConfig((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const data = await putRequest(API_ENDPOINTS.UPDATE_CONFIG, {
        admin_user_id: adminUserId,
        ...config,
      });
      if (!data.success) throw new Error(data.error || 'Failed to save config');
      setSuccess(true);
      if (data.data) {
        setConfig({
          base_reward_amount_usd: data.data.base_reward_amount_usd ?? config.base_reward_amount_usd,
          reward_mode:            data.data.reward_mode ?? config.reward_mode,
          approval_mode:          data.data.approval_mode ?? config.approval_mode,
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // ── Loading ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ width: '40px', height: '40px', border: `3px solid ${colors.border}`, borderTopColor: colors.primary, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
        <p style={{ color: colors.gray600, fontSize: '14px', margin: 0 }}>Loading configuration...</p>
      </div>
    );
  }

  // ── Fetch error ─────────────────────────────────────────────────────────────
  if (fetchError) {
    return (
      <div style={{ padding: '16px 20px', background: '#f8d7da', color: '#721c24', borderRadius: '6px', border: '1px solid #f5c6cb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
        <span style={{ fontSize: '14px' }}>{fetchError}</span>
        <button
          type="button"
          onClick={fetchConfig}
          style={{ ...commonStyles.button, ...commonStyles.buttonPrimary, padding: '7px 16px', fontSize: '13px' }}
        >
          Retry
        </button>
      </div>
    );
  }

  // ── Form ────────────────────────────────────────────────────────────────────
  return (
    <div style={{ ...commonStyles.panel, maxWidth: '560px' }} data-testid="config-panel">

      {/* Panel header, matches sectionHeader pattern */}
      <div style={commonStyles.panelHeader}>
        Referral Reward Configuration
      </div>

      <div style={{ padding: '24px' }}>

        {/* Success banner */}
        {success && (
          <div style={{ padding: '12px 16px', background: '#d4edda', color: '#155724', border: '1px solid #c3e6cb', borderRadius: '4px', marginBottom: '20px', fontSize: '14px' }} data-testid="config-success">
            ✓ Configuration saved successfully.
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div style={{ padding: '12px 16px', background: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb', borderRadius: '4px', marginBottom: '20px', fontSize: '14px' }} data-testid="config-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>

          {/* Base Reward Amount */}
          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="base_reward_amount_usd" style={labelStyle}>
              Base Reward Amount (USD)
            </label>
            <input
              id="base_reward_amount_usd"
              type="number"
              step="0.01"
              min="0"
              value={config.base_reward_amount_usd}
              onChange={(e) => handleChange('base_reward_amount_usd', e.target.value)}
              style={inputStyle}
              data-testid="config-base-reward"
              required
            />
          </div>

          {/* Reward Mode */}
          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="reward_mode" style={labelStyle}>
              Reward Mode
            </label>
            <select
              id="reward_mode"
              value={config.reward_mode}
              onChange={(e) => handleChange('reward_mode', e.target.value)}
              style={selectStyle}
              data-testid="config-reward-mode"
            >
              <option value="local_currency">Local Currency</option>
              <option value="usd">USD</option>
            </select>
          </div>

          {/* Approval Mode */}
          <div style={{ marginBottom: '28px' }}>
            <label htmlFor="approval_mode" style={labelStyle}>
              Approval Mode
            </label>
            <select
              id="approval_mode"
              value={config.approval_mode}
              onChange={(e) => handleChange('approval_mode', e.target.value)}
              style={selectStyle}
              data-testid="config-approval-mode"
            >
              <option value="manual">Manual</option>
              <option value="automatic">Automatic</option>
            </select>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={saving}
            style={{
              ...commonStyles.button,
              ...(saving ? commonStyles.buttonDisabled : commonStyles.buttonAccent),
              padding: '11px 28px',
              fontSize: '14px',
              boxShadow: saving ? 'none' : '0 2px 8px rgba(229,135,52,0.3)',
            }}
            data-testid="config-submit"
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>

        </form>
      </div>
    </div>
  );
};

export default ConfigPanel;
